import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAttendanceRegister, markAttendance, getClients } from '../services/api';
import { X, Download } from 'lucide-react';
import toast from 'react-hot-toast';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS  = ['Su','Mo','Tu','We','Th','Fr','Sa'];

// Status → short label and color
const STATUS = {
  present : { label: 'P',  bg: 'bg-green-100 text-green-700',   title: 'Present'  },
  absent  : { label: 'A',  bg: 'bg-red-100 text-red-600',       title: 'Absent'   },
  half_day: { label: 'HD', bg: 'bg-amber-100 text-amber-700',   title: 'Half Day' },
  leave   : { label: 'L',  bg: 'bg-purple-100 text-purple-700', title: 'Leave'    },
  holiday : { label: 'H',  bg: 'bg-blue-100 text-blue-700',     title: 'Holiday'  },
  week_off: { label: 'WO', bg: 'bg-gray-100 text-gray-500',     title: 'Week Off' },
};

const CYCLE = ['present','half_day','absent','leave','holiday','week_off',null];

// ── Quick-mark popover ────────────────────
const CellMenu = ({ x, y, onSelect, onClose }) => {
  return (
    <div className="fixed z-50 bg-white rounded-xl shadow-lg border border-gray-100 p-1 w-36"
      style={{ top: y, left: x }}
      onMouseLeave={onClose}>
      {Object.entries(STATUS).map(([val, { label, title, bg }]) => (
        <button key={val} onClick={() => onSelect(val)}
          className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg hover:bg-gray-50 text-left">
          <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${bg}`}>{label}</span>
          <span className="text-xs text-gray-600">{title}</span>
        </button>
      ))}
      <button onClick={() => onSelect(null)}
        className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg hover:bg-gray-50 text-left">
        <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-gray-50 text-gray-400">—</span>
        <span className="text-xs text-gray-400">Clear</span>
      </button>
    </div>
  );
};

const AttendanceRegister = () => {
  const queryClient = useQueryClient();
  const [month,     setMonth]     = useState(new Date().getMonth() + 1);
  const [year,      setYear]      = useState(new Date().getFullYear());
  const [clientId,  setClientId]  = useState('');
  const [menu,      setMenu]      = useState(null); // { x, y, depId, day }
  const tableRef = useRef(null);

  const { data: clients = [] } = useQuery({
    queryKey: ['clients-list'],
    queryFn : () => getClients({ limit: 100 }).then(r => r.data.data)
  });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['attendance-register', month, year, clientId],
    queryFn : () => getAttendanceRegister({ month, year, client_id: clientId || undefined }).then(r => r.data.data)
  });

  const markMutation = useMutation({
    mutationFn: markAttendance,
    onSuccess: () => {
      queryClient.invalidateQueries(['attendance-register', month, year, clientId]);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to mark')
  });

  const handleCellClick = (e, depId, day) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenu({ x: rect.left, y: rect.bottom + 4, depId, day });
  };

  const handleSelect = (status) => {
    if (!menu) return;
    const { depId, day } = menu;
    setMenu(null);
    if (!status) return; // clear — would need DELETE endpoint; skip for now
    const date = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    markMutation.mutate({ deployment_id: depId, attendance_date: date, status });
  };

  // Build day headers
  const daysInMonth = new Date(year, month, 0).getDate();
  const dayHeaders  = Array.from({ length: daysInMonth }, (_, i) => {
    const d    = i + 1;
    const date = new Date(year, month - 1, d);
    return { d, dow: date.getDay() };
  });

  // Group rows by client
  const grouped = rows.reduce((acc, row) => {
    if (!acc[row.client_name]) acc[row.client_name] = [];
    acc[row.client_name].push(row);
    return acc;
  }, {});

  const exportCSV = () => {
    const header = ['Worker','Code','Client','Designation',
      ...dayHeaders.map(h => h.d), 'P','A','HD','L','H','WO','Total Hrs','OT Hrs'];
    const csvRows = rows.map(row => [
      row.candidate_name, row.employee_code || '', row.client_name, row.designation || '',
      ...dayHeaders.map(h => row.days[h.d]?.status?.charAt(0).toUpperCase() || ''),
      row.summary.present, row.summary.absent, row.summary.half_day,
      row.summary.leave, row.summary.holiday, row.summary.week_off,
      row.summary.total_hours.toFixed(1), row.summary.ot_hours.toFixed(1)
    ]);
    const csv = [header, ...csvRows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `Attendance_${SHORT_MONTHS[month-1]}_${year}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" onClick={() => setMenu(null)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Attendance Register</h1>
          <p className="text-sm text-gray-400 mt-0.5">Monthly attendance grid — click any cell to mark</p>
        </div>
        <div className="flex gap-2">
          <select value={clientId} onChange={e => setClientId(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
          <select value={month} onChange={e => setMonth(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {rows.length > 0 && (
            <button onClick={exportCSV}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">
              <Download size={15} />
              Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 flex-wrap">
        {Object.entries(STATUS).map(([, { label, title, bg }]) => (
          <span key={label} className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${bg}`}>{label}</span>
            {title}
          </span>
        ))}
        <span className="text-xs text-gray-300 ml-2">Click any cell to update status</span>
      </div>

      {/* Register Table */}
      {isLoading ? (
        <div className="text-center py-16 text-gray-400">Loading register...</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <p className="text-gray-400 text-sm">No active deployments found for {MONTHS[month-1]} {year}.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto" ref={tableRef}>
            <table className="text-xs border-collapse" style={{ minWidth: `${180 + daysInMonth * 32 + 200}px` }}>
              <thead>
                {/* Month header */}
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="sticky left-0 z-20 bg-gray-50 px-3 py-2 text-left text-gray-500 font-medium w-40 border-r border-gray-100">
                    Worker
                  </th>
                  <th colSpan={daysInMonth}
                    className="px-3 py-2 text-center text-gray-700 font-semibold border-r border-gray-100">
                    {MONTHS[month - 1]} {year}
                  </th>
                  <th colSpan={6} className="px-3 py-2 text-center text-gray-500 font-medium">Summary</th>
                  <th colSpan={2} className="px-3 py-2 text-center text-gray-500 font-medium">Hours</th>
                </tr>
                {/* Day numbers */}
                <tr className="border-b border-gray-100">
                  <th className="sticky left-0 z-20 bg-white px-3 py-1.5 text-left text-gray-400 font-medium border-r border-gray-100"></th>
                  {dayHeaders.map(({ d, dow }) => (
                    <th key={d}
                      className={`w-8 text-center font-semibold py-1 px-0
                        ${dow === 0 || dow === 6 ? 'text-red-400' : 'text-gray-600'}`}>
                      {d}
                    </th>
                  ))}
                  <th className="border-l border-gray-100 px-2 py-1 text-center text-green-600 font-semibold">P</th>
                  <th className="px-2 py-1 text-center text-red-500 font-semibold">A</th>
                  <th className="px-2 py-1 text-center text-amber-600 font-semibold">HD</th>
                  <th className="px-2 py-1 text-center text-purple-600 font-semibold">L</th>
                  <th className="px-2 py-1 text-center text-blue-600 font-semibold">H</th>
                  <th className="px-2 py-1 text-center text-gray-400 font-semibold border-r border-gray-100">WO</th>
                  <th className="px-2 py-1 text-center text-gray-600 font-semibold">Hrs</th>
                  <th className="px-2 py-1 text-center text-orange-500 font-semibold">OT</th>
                </tr>
                {/* Day-of-week */}
                <tr className="border-b-2 border-gray-200">
                  <th className="sticky left-0 z-20 bg-white px-3 py-1 text-left text-gray-300 text-xs border-r border-gray-100">
                    Designation
                  </th>
                  {dayHeaders.map(({ d, dow }) => (
                    <th key={d}
                      className={`w-8 text-center text-xs py-1 font-normal
                        ${dow === 0 || dow === 6 ? 'text-red-300 bg-red-50' : 'text-gray-300'}`}>
                      {DAYS[dow]}
                    </th>
                  ))}
                  <th colSpan={8} />
                </tr>
              </thead>
              <tbody>
                {Object.entries(grouped).map(([clientName, clientRows]) => (
                  <React.Fragment key={clientName}>
                    {/* Client group header */}
                    <tr className="bg-blue-50 border-y border-blue-100">
                      <td colSpan={daysInMonth + 9}
                        className="sticky left-0 px-3 py-1.5 text-xs font-semibold text-blue-700">
                        {clientName}
                      </td>
                    </tr>
                    {clientRows.map((row, ri) => (
                      <tr key={row.deployment_id}
                        className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${ri % 2 === 1 ? 'bg-gray-50/30' : ''}`}>
                        {/* Worker name - sticky */}
                        <td className="sticky left-0 z-10 bg-white px-3 py-1.5 border-r border-gray-100 min-w-[160px]"
                          style={{ boxShadow: '2px 0 4px -2px rgba(0,0,0,0.06)' }}>
                          <p className="font-medium text-gray-900 truncate">{row.candidate_name}</p>
                          <p className="text-gray-400 truncate">{row.designation || row.employee_code || ''}</p>
                        </td>

                        {/* Day cells */}
                        {dayHeaders.map(({ d, dow }) => {
                          const cell = row.days[d];
                          const st   = cell?.status;
                          const info = st ? STATUS[st] : null;
                          return (
                            <td key={d}
                              onClick={e => handleCellClick(e, row.deployment_id, d)}
                              title={info ? `${info.title}${cell?.hours ? ` · ${Number(cell.hours).toFixed(1)}h` : ''}` : 'Click to mark'}
                              className={`w-8 text-center cursor-pointer select-none py-1 px-0 transition-colors
                                ${dow === 0 || dow === 6 ? 'bg-red-50/40' : ''}
                                ${info ? '' : 'hover:bg-blue-50'}`}>
                              {info ? (
                                <span className={`inline-block text-xs font-bold px-0.5 rounded ${info.bg}`}>
                                  {info.label}
                                </span>
                              ) : (
                                <span className="text-gray-200 text-xs">·</span>
                              )}
                            </td>
                          );
                        })}

                        {/* Summary */}
                        <td className="border-l border-gray-100 px-2 py-1.5 text-center font-semibold text-green-600">{row.summary.present}</td>
                        <td className="px-2 py-1.5 text-center text-red-500">{row.summary.absent}</td>
                        <td className="px-2 py-1.5 text-center text-amber-600">{row.summary.half_day || '—'}</td>
                        <td className="px-2 py-1.5 text-center text-purple-600">{row.summary.leave || '—'}</td>
                        <td className="px-2 py-1.5 text-center text-blue-600">{row.summary.holiday || '—'}</td>
                        <td className="px-2 py-1.5 text-center text-gray-400 border-r border-gray-100">{row.summary.week_off || '—'}</td>
                        <td className="px-2 py-1.5 text-center text-gray-700">{row.summary.total_hours.toFixed(0)}</td>
                        <td className="px-2 py-1.5 text-center text-orange-500">{row.summary.ot_hours > 0 ? row.summary.ot_hours.toFixed(1) : '—'}</td>
                      </tr>
                    ))}
                    {/* Client subtotal */}
                    <tr className="bg-gray-50 border-b-2 border-gray-200 text-xs font-semibold text-gray-500">
                      <td className="sticky left-0 bg-gray-50 px-3 py-1.5 border-r border-gray-100">
                        {clientName} Total ({clientRows.length} workers)
                      </td>
                      {dayHeaders.map(({ d }) => {
                        const presentCount = clientRows.filter(r => r.days[d]?.status === 'present').length;
                        return (
                          <td key={d} className="w-8 text-center py-1 text-gray-400">
                            {presentCount > 0 ? presentCount : ''}
                          </td>
                        );
                      })}
                      <td className="border-l border-gray-100 px-2 py-1.5 text-center text-green-600">
                        {clientRows.reduce((s, r) => s + r.summary.present, 0)}
                      </td>
                      <td className="px-2 py-1.5 text-center text-red-500">
                        {clientRows.reduce((s, r) => s + r.summary.absent, 0)}
                      </td>
                      <td colSpan={6} />
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cell menu */}
      {menu && (
        <CellMenu x={menu.x} y={menu.y} onSelect={handleSelect} onClose={() => setMenu(null)} />
      )}
    </div>
  );
};

export default AttendanceRegister;
